import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import {
  AcceptQuote,
  CreateQuoteLine,
  Quote,
  QuoteApiService,
  QuoteStatus,
  QUOTE_STATUS_LABELS,
  QUOTE_TERMINAL,
  QUOTE_TRANSITIONS,
  TransactionCode,
} from '../../../services/quote-api.service';

/** A line being typed. Amount is a string because an empty number input is
 *  null and a half-typed one is NaN, and neither is a price. */
interface DraftLine {
  description: string;
  amount: string;
}

/**
 * The lead's paperwork: its quotes, and the contract accepting one produces.
 *
 * This tab used to list the CUSTOMER's contracts, which a lead does not have.
 * A contract needs a customer account and a lead has none until a quote is
 * accepted - the account is created inside that transaction. So the tab showed
 * an explanation and offered nothing, while the quote API it should have been
 * driving had no screen anywhere in the product.
 *
 * ── WHAT IS AND IS NOT A TRANSITION ───────────────────────────────────────
 *
 * Draft ⇄ Internal review, Approved → Sent → Viewed, and anything → Expired or
 * Rejected are plain moves. Approving and accepting are not: approve is a
 * decision under its own permission (`Automation.Approve`, which working a
 * quote does not grant), and accept takes a transaction code and a term and
 * WRITES A CONTRACT. Offering either as a status button would put them behind
 * a command that refuses them.
 */
@Component({
  selector: 'app-lead-quotes',
  templateUrl: './lead-quotes.component.html',
  styleUrls: ['./lead-quotes.component.css'],
})
export class LeadQuotesComponent implements OnChanges {
  @Input() leadId: number | null = null;
  /** `LeadPipeline.Manage`: raise a quote, move it, accept it. */
  @Input() canManage = false;
  /** `Automation.Approve`: the internal yes. Separate on purpose. */
  @Input() canApprove = false;
  /** Accepting wins the lead and creates the account, so the record around
   *  this tab is stale the moment it succeeds. */
  @Output() changed = new EventEmitter<void>();

  quotes: Quote[] = [];
  loading = false;
  loadError: string | null = null;
  /** The quote a command is in flight for, so only its own buttons go quiet. */
  busyId: number | null = null;
  commandError: string | null = null;

  readonly statusLabels = QUOTE_STATUS_LABELS;

  // ── Raising one ─────────────────────────────────────────────────────────
  composing = false;
  draftLines: DraftLine[] = [{ description: '', amount: '' }];
  draftValidUntil = '';

  /**
   * What is being quoted, in the terms a contract states them.
   *
   * The same vocabulary the contract dialog uses - PerService / FlatMonthly -
   * because a quote that says one thing and the contract accepting it says
   * another is two documents about one arrangement.
   */
  draftTerms = {
    title: '',
    terms: '',
    effectiveFrom: '',
    effectiveTo: '',
    billingMethod: '',
    billingDayOfMonth: null as number | null,
    expectedMonthlyCount: null as number | null,
    poReleaseNumber: '',
    transactionCodeId: null as number | null,
  };

  /** The stored value under the word a person reads. */
  billingLabel(value: string): string {
    return this.billingMethods.find(m => m.value === value)?.label ?? value;
  }

  readonly billingMethods = [
    { value: 'PerService', label: 'Per service' },
    { value: 'FlatMonthly', label: 'Flat monthly' },
  ];
  creating = false;

  // ── Accepting one ───────────────────────────────────────────────────────
  acceptingId: number | null = null;
  transactionCodes: TransactionCode[] = [];
  acceptDraft = { transactionCodeId: null as number | null, effectiveFrom: '', effectiveTo: '' };

  constructor(private readonly api: QuoteApiService) {}

  ngOnChanges(): void {
    this.load();
  }

  load(): void {
    if (this.leadId == null) { this.quotes = []; return; }
    const requested = this.leadId;
    this.loading = true;
    this.loadError = null;
    this.api.forLead(requested).subscribe({
      next: quotes => {
        if (this.leadId !== requested) return;
        // Newest first: a lead with five revisions is read from the top, and
        // the server returns them oldest-first as a version series.
        this.quotes = [...quotes].sort((a, b) => b.version - a.version);
        this.loading = false;
      },
      error: () => {
        if (this.leadId !== requested) return;
        this.loadError = 'Could not load the quotes for this lead.';
        this.loading = false;
      },
    });
  }

  statusLabel(status: QuoteStatus): string {
    return QUOTE_STATUS_LABELS[status] ?? status;
  }

  /** Plain moves only. Approve and accept are their own buttons. */
  movesFrom(quote: Quote): QuoteStatus[] {
    return this.canManage ? QUOTE_TRANSITIONS[quote.status] ?? [] : [];
  }

  isTerminal(quote: Quote): boolean {
    return QUOTE_TERMINAL.includes(quote.status);
  }

  /** The internal yes is offered only where it can land, and only to the
   *  permission that carries it. */
  canApproveNow(quote: Quote): boolean {
    return this.canApprove && quote.status === 'InternalReview';
  }

  /**
   * Accepting is offered once the customer has it - Sent or Viewed.
   *
   * Not on Approved: approved means WE agreed to offer it, and a quote nobody
   * has been given cannot have been said yes to. Recording an acceptance there
   * would write a contract against an offer that was never made.
   */
  canAcceptNow(quote: Quote): boolean {
    return this.canManage && (quote.status === 'Sent' || quote.status === 'Viewed');
  }

  // ── Raising ─────────────────────────────────────────────────────────────

  startCompose(): void {
    this.composing = true;
    this.commandError = null;
    this.draftLines = [{ description: '', amount: '' }];
    this.draftValidUntil = '';
    this.draftTerms = {
      title: '', terms: '', effectiveFrom: '', effectiveTo: '',
      billingMethod: '', billingDayOfMonth: null, expectedMonthlyCount: null,
      poReleaseNumber: '', transactionCodeId: null,
    };
  }

  cancelCompose(): void {
    this.composing = false;
    this.draftLines = [{ description: '', amount: '' }];
  }

  addLine(): void {
    this.draftLines = [...this.draftLines, { description: '', amount: '' }];
  }

  removeLine(index: number): void {
    this.draftLines = this.draftLines.filter((_, at) => at !== index);
    if (!this.draftLines.length) this.draftLines = [{ description: '', amount: '' }];
  }

  /** The lines that are actually priced. A blank row a person tabbed past is
   *  not a zero-value item. */
  get draftPricedLines(): CreateQuoteLine[] {
    return this.draftLines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => LeadQuotesComponent.amountOf(line.amount) != null)
      .map(({ line, index }) => ({
        // The server owns line identity; a stable key per row is enough for it
        // to tell two lines apart within one quote.
        lineKey: `line-${index + 1}`,
        monthlyAmount: LeadQuotesComponent.amountOf(line.amount)!,
        description: line.description.trim() || null,
      }));
  }

  get draftTotal(): number {
    return this.draftPricedLines.reduce((sum, line) => sum + line.monthlyAmount, 0);
  }

  get draftValid(): boolean {
    return this.draftPricedLines.length > 0;
  }

  /** A price, or null for anything that is not one - blank, spaces, letters,
   *  a lone minus sign mid-typing. `Number('')` is 0, which would price a
   *  line at nothing rather than leaving it out. */
  static amountOf(raw: string): number | null {
    const text = raw.trim();
    if (!text) return null;
    const value = Number(text);
    return Number.isFinite(value) ? value : null;
  }

  createQuote(): void {
    if (this.leadId == null || !this.draftValid || this.creating) return;
    this.creating = true;
    this.commandError = null;
    this.api.create({
      leadId: this.leadId,
      lines: this.draftPricedLines,
      termsVersion: null,
      validUntil: this.draftValidUntil || null,
      title: this.draftTerms.title.trim() || null,
      terms: this.draftTerms.terms.trim() || null,
      effectiveFrom: this.draftTerms.effectiveFrom || null,
      effectiveTo: this.draftTerms.effectiveTo || null,
      billingMethod: this.draftTerms.billingMethod || null,
      billingDayOfMonth: this.draftTerms.billingDayOfMonth,
      expectedMonthlyCount: this.draftTerms.expectedMonthlyCount,
      poReleaseNumber: this.draftTerms.poReleaseNumber.trim() || null,
      transactionCodeId: this.draftTerms.transactionCodeId,
    }).subscribe({
      next: () => {
        this.creating = false;
        this.composing = false;
        this.draftLines = [{ description: '', amount: '' }];
        this.load();
      },
      error: error => {
        this.creating = false;
        this.commandError = this.reasonFor(error, 'The quote could not be raised.');
      },
    });
  }

  // ── Moving ──────────────────────────────────────────────────────────────

  move(quote: Quote, to: QuoteStatus): void {
    if (this.busyId != null) return;
    this.busyId = quote.id;
    this.commandError = null;
    this.api.transition(quote.id, to).subscribe({
      next: () => { this.busyId = null; this.load(); },
      error: error => {
        this.busyId = null;
        this.commandError = this.reasonFor(error, 'That move was refused.');
      },
    });
  }

  approve(quote: Quote, approve: boolean): void {
    if (this.busyId != null) return;
    this.busyId = quote.id;
    this.commandError = null;
    this.api.approve(quote.id, approve, null).subscribe({
      next: () => { this.busyId = null; this.load(); },
      error: error => {
        this.busyId = null;
        this.commandError = this.reasonFor(error, 'The decision could not be recorded.');
      },
    });
  }

  // ── Accepting ───────────────────────────────────────────────────────────

  startAccept(quote: Quote): void {
    this.acceptingId = quote.id;
    this.commandError = null;
    const today = LeadQuotesComponent.todayIso();
    // Defaults come from the quote itself. A quote that stated its service
    // period and its billing code has already answered these; asking again is
    // how the contract ends up disagreeing with the paper it came from. Still
    // editable - accepting on different terms than were quoted is a real
    // thing, it just should not be the path of least resistance.
    this.acceptDraft = {
      transactionCodeId: quote.transactionCodeId,
      effectiveFrom: quote.effectiveFrom ?? today,
      // The contract's own "no expiry", which the CRM dialog uses too.
      effectiveTo: quote.effectiveTo ?? '9999-12-31',
    };
    if (!this.transactionCodes.length) {
      this.api.transactionCodes().subscribe({
        next: codes => { this.transactionCodes = codes; },
        error: () => {
          this.commandError =
            'Could not load the billing codes, and a contract needs one.';
        },
      });
    }
  }

  cancelAccept(): void {
    this.acceptingId = null;
  }

  get acceptValid(): boolean {
    return this.acceptDraft.transactionCodeId != null
      && !!this.acceptDraft.effectiveFrom
      && !!this.acceptDraft.effectiveTo;
  }

  confirmAccept(quote: Quote): void {
    if (!this.acceptValid || this.busyId != null) return;
    this.busyId = quote.id;
    this.commandError = null;
    const request: AcceptQuote = {
      transactionCodeId: this.acceptDraft.transactionCodeId!,
      effectiveFrom: this.acceptDraft.effectiveFrom,
      effectiveTo: this.acceptDraft.effectiveTo,
    };
    this.api.accept(quote.id, request).subscribe({
      next: () => {
        this.busyId = null;
        this.acceptingId = null;
        this.load();
        // Accepting wins the lead and creates the customer account, so every
        // other tab is now describing a record that has moved on.
        this.changed.emit();
      },
      error: error => {
        this.busyId = null;
        this.commandError = this.reasonFor(error, 'The quote could not be accepted.');
      },
    });
  }

  static todayIso(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
      + `-${String(now.getDate()).padStart(2, '0')}`;
  }

  /**
   * The server's own words where it gave any.
   *
   * Quote commands answer a refusal with `{ error }` - "the lead is Lost; a
   * closed lead cannot accept a quote" says something a generic message never
   * could, and this screen has no business paraphrasing it.
   */
  private reasonFor(error: { status?: number; error?: { error?: string } }, fallback: string): string {
    return error?.error?.error
      ?? (error?.status === 403 ? 'You do not have permission to do that.' : fallback);
  }
}
